import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles, faqs } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useState } from 'react';

export default function Home() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const featuredArticles = articles.filter((a) => a.featured);

  const pageMeta = {
    title: `${siteInfo.name}｜正版同人手册推荐`,
    description: siteInfo.description,
    keywords: siteInfo.keywords,
    canonicalUrl: siteInfo.url,
    ogType: 'website',
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteInfo.url}/#organization`,
        name: siteInfo.name,
        url: siteInfo.url,
        email: siteInfo.email,
        telephone: siteInfo.phone,
        address: {
          '@type': 'PostalAddress',
          addressLocality: '上海',
          addressCountry: 'CN',
          streetAddress: '南京西路1788号动漫文化中心3F',
        },
        foundingDate: siteInfo.foundingYear,
      },
      {
        '@type': 'WebSite',
        '@id': `${siteInfo.url}/#website`,
        url: siteInfo.url,
        name: siteInfo.name,
        description: siteInfo.description,
        publisher: { '@id': `${siteInfo.url}/#organization` },
      },
      {
        '@type': 'WebPage',
        '@id': `${siteInfo.url}/#webpage`,
        url: siteInfo.url,
        name: `${siteInfo.name} - 首页`,
        description: siteInfo.description,
        isPartOf: { '@id': `${siteInfo.url}/#website` },
        about: { '@id': `${siteInfo.url}/#organization` },
      },
      {
        '@type': 'FAQPage',
        '@id': `${siteInfo.url}/#faq`,
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEOHead meta={pageMeta} />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />

      <main>
        <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Japanese%20anime%20style%20bookstore%20interior%20with%20wooden%20shelves%20filled%20with%20colorful%20manga%20and%20doujinshi%20books%2C%20warm%20ambient%20lighting%20from%20hanging%20lanterns%2C%20cozy%20reading%20atmosphere%2C%20soft%20natural%20light%20from%20large%20windows%2C%20artistic%20composition%2C%20warm%20cream%20and%20wood%20tones%2C%20harmonious%20indoor%20scene&width=1800&height=1200&seq=home-hero-2026&orientation=landscape"
            alt="动漫本子收藏指南 - 正版同人志收藏"
            title="动漫本子收藏指南｜正版同人手册推荐"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              探索动漫本子的精彩世界
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              正版同人志、角色设定集、漫画手册——为每一位动漫爱好者打造的专属收藏指南
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>浏览分类</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-full font-medium text-sm hover:bg-white/30 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>了解更多</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-information-line"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                分类浏览
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                按类别探索你感兴趣的内容，找到最适合你的收藏方向
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category?tab=${cat.slug}`}
                  className="group bg-background-100 rounded-lg p-5 transition-all duration-300 hover:bg-background-200/70 cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors shrink-0">
                      <i className={`${cat.icon} text-xl text-primary-600`}></i>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5 whitespace-nowrap">
                        {cat.name}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {cat.description}
                      </p>
                      <p className="text-xs text-foreground-500 mt-2">{cat.articleCount} 篇文章</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-600">不容错过的优质内容</p>
              </div>
              <Link
                to="/category"
                className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <span>查看全部</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/30 cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                      <span className="text-xs px-2.5 py-1 bg-accent-100 text-accent-800 rounded-full font-medium whitespace-nowrap">
                        {article.categoryName}
                      </span>
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200/70">
                      <span className="text-xs text-foreground-500">{article.author}</span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600">关于同人本收藏，你想知道的都在这里</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-background-200/50 transition-colors"
                    onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                  >
                    <span className="font-medium text-sm md:text-base text-foreground-900 pr-4">
                      {faq.question}
                    </span>
                    <div className={`w-5 h-5 flex items-center justify-center shrink-0 transition-transform duration-200 ${activeFaq === index ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-600"></i>
                    </div>
                  </button>
                  {activeFaq === index && (
                    <div className="px-5 pb-5">
                      <div className="border-t border-background-200/70 pt-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-16 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cozy%20reading%20corner%20with%20manga%20books%20and%20soft%20cushions%2C%20gentle%20window%20light%2C%20Japanese%20interior%20design%20elements%2C%20peaceful%20atmosphere%2C%20artistic%20composition%2C%20soft%20cream%20and%20wood%20tones&width=1800&height=800&seq=home-cta-2026&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
            aria-hidden="true"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/45"></div>
          <div className="relative z-10 max-w-2xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              开启你的收藏之旅
            </h2>
            <p className="text-sm md:text-base text-white/80 mb-8 leading-relaxed">
              无论你是刚入坑的新手还是经验丰富的老收藏家，这里都有你想要的内容。加入我们，一起探索动漫本子的无限魅力。
            </p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-background-50 dark:text-foreground-950 rounded-full font-medium text-sm hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <span>立即开始探索</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-compass-3-line"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}